import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, Sparkles, Search, MessageCircle, Instagram, Facebook, Smartphone, Mail, QrCode,
  User, Bot, Filter, Radio, PauseCircle, PlayCircle, UserCheck, Ticket, ChevronDown, Loader2,
  BellRing, Volume2, VolumeX, ChevronLeft, Pencil, X, LifeBuoy,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAlerts } from '../contexts/AlertsContext';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { EmptyState } from '../components/EmptyState';
import { parseSticker, STICKER_MAP, stickerSrc } from '../lib/noahStickers';
import { errorText } from '../lib/errorText';
import { NoahTasks } from '../components/NoahTasks';
import { StickerPicker } from '../components/StickerPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MODE_LABEL = { auto: 'Automático', assist: 'Copiloto', manual: 'Manual' };

const CHANNEL_ICON = {
  whatsapp:     { Icon: MessageCircle, color: '#25D366', label: 'WA Cloud' },
  whatsapp_web: { Icon: QrCode,        color: '#128C7E', label: 'WA Web'   },
  messenger:    { Icon: Facebook,      color: '#0084FF', label: 'Messenger'},
  instagram:    { Icon: Instagram,     color: '#E1306C', label: 'Instagram'},
  sms:          { Icon: Smartphone,    color: '#F22F46', label: 'SMS'      },
  email:        { Icon: Mail,          color: '#3B82F6', label: 'Email'    },
  tiktok:       { Icon: Radio,         color: '#000000', label: 'TikTok'   },
};

const NOAH_MODE_BADGE = {
  auto:   { text: 'Auto',    style: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30' },
  assist: { text: 'Asistido', style: 'bg-brand-green/15 text-brand-deep dark:text-brand-green border-brand-green/30' },
  manual: { text: 'Manual',  style: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30' },
};

const HANDLE_BADGE = {
  noah:  { text: 'Noah',    style: 'bg-violet-500/15 text-brand-deep dark:text-brand-deep dark:text-brand-green' },
  human: { text: 'Humano',  style: 'bg-orange-500/15 text-amber-700 dark:text-amber-400' },
};

export default function Inbox() {
  const navigate = useNavigate();
  const { urgentCount, muted, toggleMute } = useAlerts();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: '', description: '', priority: 'normal', category: 'support' });
  const [filters, setFilters] = useState({ platform: 'all', campaign_id: 'all', handled_by: 'all', q: '' });
  const messagesEndRef = useRef(null);

  // -------- Data loaders
  const loadInbox = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.platform !== 'all') params.append('platform', filters.platform);
      if (filters.campaign_id !== 'all') params.append('campaign_id', filters.campaign_id);
      if (filters.handled_by !== 'all') params.append('handled_by', filters.handled_by);
      if (filters.q) params.append('q', filters.q);
      const res = await axios.get(`${API}/inbox/conversations?${params.toString()}`);
      setConversations(res.data.conversations || []);
    } catch {
      toast.error('No se pudo cargar el inbox');
    } finally {
      setLoading(false);
    }
  }, [filters.platform, filters.campaign_id, filters.handled_by, filters.q]);

  const loadMessages = useCallback(async (chatId) => {
    if (!chatId) return;
    try {
      const res = await axios.get(`${API}/chats/${chatId}/messages`);
      setMessages(Array.isArray(res.data) ? res.data : (res.data.messages || []));
    } catch {
      setMessages([]);
    }
  }, []);

  const loadCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns`);
      setCampaigns(res.data || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => { loadInbox(); }, [loadInbox]);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);
  useEffect(() => { loadMessages(selectedChatId); }, [selectedChatId, loadMessages]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [messages]);

  const selected = conversations.find((c) => c.chat_id === selectedChatId);

  // -------- Actions
  const handleSend = async () => {
    if (!reply.trim() || !selectedChatId) return;
    setSending(true);
    try {
      await axios.post(`${API}/chats/${selectedChatId}/messages`, {
        content: reply, sender: 'agent', platform: selected?.platform || 'whatsapp',
      });
      setReply('');
      await loadMessages(selectedChatId);
      await loadInbox();
    } catch (e) {
      toast.error(errorText(e, 'Error al enviar'));
    } finally {
      setSending(false);
    }
  };

  const handleSendSticker = async (sticker) => {
    if (!selectedChatId) return;
    setSending(true);
    try {
      await axios.post(`${API}/chats/${selectedChatId}/messages`, {
        content: `${sticker.text} [[sticker:${sticker.key}]]`,
        sender: 'agent',
        platform: selected?.platform || 'whatsapp',
      });
      await loadMessages(selectedChatId);
      await loadInbox();
    } catch (e) {
      toast.error(errorText(e, 'No se pudo enviar el sticker'));
    } finally {
      setSending(false);
    }
  };

  const handleTakeover = async () => {
    if (!selectedChatId) return;
    try {
      await axios.post(`${API}/chats/${selectedChatId}/takeover`, { reason: 'Intervención humana' });
      toast.success('Tomaste el control · Noah pausada');
      await loadInbox();
    } catch { toast.error('No se pudo tomar el control'); }
  };

  const handleRelease = async () => {
    if (!selectedChatId) return;
    try {
      await axios.post(`${API}/chats/${selectedChatId}/release`, {});
      toast.success('Noah vuelve a atender');
      await loadInbox();
    } catch { toast.error('No se pudo liberar'); }
  };

  const handleChangeNoahMode = async (mode) => {
    if (!selected?.campaign?.id) return toast.warning('Este chat no tiene campaña vinculada');
    try {
      await axios.put(`${API}/campaigns/${selected.campaign.id}/noah-mode`, { mode });
      toast.success(`Modo Noah: ${MODE_LABEL[mode] || mode}`);
      await loadInbox();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo cambiar el modo'); }
  };

  const handleSuggest = async () => {
    if (!selectedChatId) return;
    setSuggesting(true);
    try {
      await axios.post(`${API}/chats/${selectedChatId}/suggest`);
      await loadInbox();
      toast.success('Noah preparó una sugerencia');
    } catch (e) { toast.error(errorText(e, 'Noah no pudo sugerir')); } finally { setSuggesting(false); }
  };

  const handleAcceptSuggestion = async (text) => {
    if (!selectedChatId) return;
    setSending(true);
    try {
      await axios.post(`${API}/chats/${selectedChatId}/suggestion/accept`, text ? { text } : {});
      setReply('');
      toast.success('Respuesta enviada al cliente');
      await loadMessages(selectedChatId);
      await loadInbox();
    } catch (e) { toast.error(errorText(e, 'No se pudo enviar')); } finally { setSending(false); }
  };

  const handleDiscardSuggestion = async () => {
    if (!selectedChatId) return;
    try { await axios.delete(`${API}/chats/${selectedChatId}/suggestion`); await loadInbox(); } catch { /* silent */ }
  };

  const handleCreateTicket = async () => {
    if (!selected) return;
    try {
      await axios.post(`${API}/tickets`, {
        customer_id: selected.customer?.id,
        chat_id: selected.chat_id,
        campaign_id: selected.campaign?.id,
        subject: ticketForm.subject,
        description: ticketForm.description,
        priority: ticketForm.priority,
        category: ticketForm.category,
      });
      toast.success('Ticket creado');
      setTicketOpen(false);
      setTicketForm({ subject: '', description: '', priority: 'normal', category: 'support' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear ticket'); }
  };

  return (
    <div data-testid="unified-inbox-page" className="min-h-screen">
      <Header title="Bandeja unificada" subtitle="Todos tus canales · Noah en tiempo real · Multi-campaña" />

      <div className="flex h-[calc(100vh-4rem)]">
        {/* ---------- LEFT: filter + list ---------- */}
        <aside className={cn(
          'w-full sm:w-96 sm:shrink-0 border-r border-border flex-col bg-background/40 backdrop-blur',
          selectedChatId ? 'hidden sm:flex' : 'flex'
        )}>
          {/* Aviso de tickets urgentes */}
          <button
            data-testid="inbox-urgent-badge"
            onClick={() => navigate('/tickets')}
            className={cn(
              'mx-3 mt-3 p-2.5 rounded-xl border flex items-center gap-2 text-left transition-colors',
              urgentCount > 0
                ? 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20'
                : 'bg-muted/20 border-border/50 hover:bg-muted/40'
            )}
          >
            <span className="relative mr-2">
              <BellRing className={cn('w-4 h-4', urgentCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground')} />
              {urgentCount > 0 && (
                <span className="absolute -top-2 -right-2.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
                  data-testid="inbox-urgent-count">
                  {urgentCount}
                </span>
              )}
            </span>
            <span className="text-xs flex-1">
              {urgentCount > 0
                ? `${urgentCount} ticket${urgentCount > 1 ? 's' : ''} urgente${urgentCount > 1 ? 's' : ''} sin resolver`
                : 'Sin tickets urgentes'}
            </span>
            <span
              role="button"
              tabIndex={0}
              data-testid="inbox-mute-toggle"
              title={muted ? 'Activar sonido de avisos' : 'Silenciar avisos'}
              onClick={(e) => { e.stopPropagation(); toggleMute(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); toggleMute(); } }}
              className="p-1 rounded-lg hover:bg-background/60 transition-colors"
            >
              {muted ? <VolumeX className="w-4 h-4 text-muted-foreground" /> : <Volume2 className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />}
            </span>
          </button>

          {/* Filters */}
          <div className="p-3 space-y-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="inbox-search"
                placeholder="Buscar cliente…"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                className="pl-9 h-9 bg-muted border-none"
              />
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <Select value={filters.platform} onValueChange={(v) => setFilters((f) => ({ ...f, platform: v }))}>
                <SelectTrigger data-testid="filter-platform" className="h-8 text-xs"><SelectValue placeholder="Canal" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos canales</SelectItem>
                  {Object.entries(CHANNEL_ICON).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.campaign_id} onValueChange={(v) => setFilters((f) => ({ ...f, campaign_id: v }))}>
                <SelectTrigger data-testid="filter-campaign" className="h-8 text-xs"><SelectValue placeholder="Campaña" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas campañas</SelectItem>
                  {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.handled_by} onValueChange={(v) => setFilters((f) => ({ ...f, handled_by: v }))}>
                <SelectTrigger data-testid="filter-handled" className="h-8 text-xs"><SelectValue placeholder="Atendido por" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="noah">Noah</SelectItem>
                  <SelectItem value="human">Humano</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* List */}
          <ScrollArea className="flex-1 w-full [&>div>div]:!block">
            {loading ? (
              <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : conversations.length === 0 ? (
              <EmptyState
                mood="chat"
                size={110}
                compact
                title="Sin conversaciones"
                description="Cuando entre un mensaje por WhatsApp, Messenger, Instagram o SMS aparecerá aquí."
                testId="inbox-empty"
              />
            ) : (
              <div className="p-2 space-y-1">
                {conversations.map((c) => {
                  const CH = CHANNEL_ICON[c.platform] || CHANNEL_ICON.email;
                  const isActive = c.chat_id === selectedChatId;
                  const hb = HANDLE_BADGE[c.handled_by] || HANDLE_BADGE.noah;
                  return (
                    <div
                      key={c.chat_id}
                      data-testid={`conv-item-${c.chat_id}`}
                      onClick={() => setSelectedChatId(c.chat_id)}
                      className={cn(
                        'p-3 rounded-xl cursor-pointer border transition-all',
                        isActive ? 'bg-primary/10 border-primary/30' : 'border-transparent hover:bg-muted/40'
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <div className="relative shrink-0">
                          <Avatar className="h-9 w-9"><AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">{(c.customer?.name || '?').charAt(0)}</AvatarFallback></Avatar>
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center border-2 border-background" style={{ background: `${CH.color}22` }}>
                            <CH.Icon className="w-3 h-3" style={{ color: CH.color }} />
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold truncate">{c.customer?.name || 'Sin nombre'}</p>
                            <span className="text-[10px] text-muted-foreground shrink-0">
                              {c.updated_at ? new Date(c.updated_at).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{c.last_message || 'Sin mensajes'}</p>
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            <Badge className={cn('text-[9px] h-4 px-1', hb.style)}>{hb.text}</Badge>
                            {c.campaign?.name && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1 truncate max-w-[6rem]">{c.campaign.name}</Badge>
                            )}
                            {c.needs_human && <Badge className="text-[9px] h-4 px-1 bg-amber-500 text-white" data-testid={`needs-human-${c.chat_id}`}>Ayuda</Badge>}
                            {c.noah_suggestion?.text && <Badge className="text-[9px] h-4 px-1 bg-brand-green/20 text-brand-deep dark:text-brand-green" data-testid={`has-suggestion-${c.chat_id}`}>Sugerencia</Badge>}
                            {c.ai_paused && <Badge className="text-[9px] h-4 px-1 bg-amber-500/15 text-amber-700 dark:text-amber-400">Pausa</Badge>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </aside>

        {/* ---------- CENTER: chat + actions ---------- */}
        <main className={cn('flex-1 min-w-0 flex-col', selectedChatId ? 'flex' : 'hidden sm:flex')}>
          {!selected ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-primary/20 flex items-center justify-center">
                  <Sparkles className="w-10 h-10 text-brand-deep dark:text-brand-green/60" />
                </div>
                <h3 className="text-lg font-semibold">Selecciona una conversación</h3>
                <p className="text-sm text-muted-foreground">Elige una conversación del panel izquierdo</p>
              </div>
            </div>
          ) : (
            <>
              {/* header */}
              <div className="h-16 px-4 flex items-center justify-between bg-primary text-primary-foreground">
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    data-testid="inbox-back-to-list"
                    onClick={() => setSelectedChatId(null)}
                    className="sm:hidden -ml-1 p-1 rounded-lg hover:bg-white/15 transition-colors"
                    aria-label="Volver a la lista"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <Avatar className="h-10 w-10 ring-2 ring-white/40 shrink-0">
                    <AvatarFallback className="bg-white/20 text-white font-bold">{(selected.customer?.name || '?').charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p data-testid="chat-customer-name" className="font-bold truncate">{selected.customer?.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-white/80 flex-wrap">
                      <Badge className="bg-white/20 text-white border-none h-4 px-1.5 text-[10px]">
                        {CHANNEL_ICON[selected.platform]?.label || selected.platform}
                      </Badge>
                      {selected.customer?.phone && <span>{selected.customer.phone}</span>}
                      {selected.campaign?.name && <span>· {selected.campaign.name}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {selected.campaign?.id && (
                    <Select value={selected.campaign.noah_mode || 'auto'} onValueChange={handleChangeNoahMode}>
                      <SelectTrigger data-testid="noah-mode-toggle" className="h-8 w-28 bg-white/20 text-white border-white/30 text-xs">
                        <SelectValue placeholder="Modo Noah" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Automático</SelectItem>
                        <SelectItem value="assist">Copiloto</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {selected.handled_by === 'human' ? (
                    <Button data-testid="release-btn" variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={handleRelease}>
                      <PlayCircle className="w-4 h-4 mr-1" /> Liberar
                    </Button>
                  ) : (
                    <Button data-testid="takeover-btn" variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={handleTakeover}>
                      <UserCheck className="w-4 h-4 mr-1" /> Tomar
                    </Button>
                  )}
                  <Button data-testid="open-ticket-btn" variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => setTicketOpen(true)}>
                    <Ticket className="w-4 h-4 mr-1" /> Ticket
                  </Button>
                </div>
              </div>

              {/* messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((m) => {
                    const outgoing = m.sender === 'user' || m.sender === 'agent';
                    const parsed = parseSticker(m.content || '');
                    const stickerKey = m.sticker || parsed.key;
                    const bodyText = m.sticker ? (m.content || '') : parsed.text;
                    return (
                      <div key={m.id || m.timestamp} className={cn('flex gap-2', outgoing ? 'flex-row-reverse' : '')}>
                        <Avatar className="h-8 w-8 shrink-0">
                          <AvatarFallback className={cn(
                            m.sender === 'ai' ? 'bg-primary' :
                            m.sender === 'customer' ? 'bg-brand-green' : 'bg-emerald-500'
                          )}>
                            {m.sender === 'ai' ? <Sparkles className="w-3.5 h-3.5 text-white" /> :
                             m.sender === 'customer' ? <User className="w-3.5 h-3.5 text-white" /> :
                             <span className="text-white text-[10px] font-bold">Tú</span>}
                          </AvatarFallback>
                        </Avatar>
                        <div className="max-w-[70%]">
                          {m.sender === 'ai' && (
                            <div className="text-xs text-brand-deep dark:text-brand-deep dark:text-brand-green mb-1 flex items-center gap-1">
                              <Sparkles className="w-3 h-3" /> Noah
                              {m.voice_score && (
                                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-500/15 text-brand-deep dark:text-brand-deep dark:text-brand-green">
                                  ✨ {m.voice_score.score}%
                                </span>
                              )}
                            </div>
                          )}
                          {stickerKey && (
                            <img
                              src={stickerSrc(stickerKey)}
                              alt={STICKER_MAP[stickerKey]?.text || 'Sticker de Noah'}
                              data-testid={`chat-sticker-${stickerKey}`}
                              className={cn('w-28 sm:w-32 h-auto mb-1 drop-shadow-md', outgoing && 'ml-auto')}
                            />
                          )}
                          {bodyText && (
                            <div className={cn(
                              'rounded-2xl px-3 py-2 text-sm',
                              outgoing ? 'bg-primary text-primary-foreground rounded-tr-sm' :
                              m.sender === 'ai' ? 'bg-primary/15 border border-primary/20 rounded-tl-sm' :
                              'bg-muted rounded-tl-sm'
                            )}>
                              {bodyText}
                            </div>
                          )}
                          <p className="text-[10px] text-muted-foreground mt-0.5 px-1">
                            {m.timestamp ? new Date(m.timestamp).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {messages.length === 0 && (
                    <div className="text-center py-10 text-sm text-muted-foreground">Sin mensajes en esta conversación</div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* reply */}
              <div className="p-3 border-t border-border">
                <div className="mb-2"><NoahTasks customerId={selected.customer?.id} campaignId={selected.campaign?.id} compact /></div>
                {selected.unassigned && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2 p-2 rounded-2xl bg-brand-butter/30 border border-brand-copper/30 text-xs" data-testid="unassigned-banner">
                    <p className="flex-1 font-semibold text-brand-deep dark:text-brand-butter">Conversación sin clasificar · elige la campaña que la atenderá</p>
                    <Select onValueChange={async (v) => { try { await axios.post(`${API}/chats/${selectedChatId}/assign-campaign`, { campaign_id: v }); toast.success('Campaña asignada · Noah ya puede responder'); await loadInbox(); await loadMessages(selectedChatId); } catch (e) { toast.error(errorText(e, 'No se pudo asignar')); } }}>
                      <SelectTrigger className="h-8 w-full sm:w-56 text-xs" data-testid="assign-campaign-select"><SelectValue placeholder="Asignar campaña" /></SelectTrigger>
                      <SelectContent>{campaigns.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {selected.needs_human && !selected.unassigned && (
                  <div className="flex items-start gap-2 mb-2 p-2 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-xs" data-testid="handoff-banner">
                    <LifeBuoy className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-amber-700 dark:text-amber-400">Noah pidió ayuda humana</p>
                      <p className="text-muted-foreground">{selected.handoff_reason || 'No encontró la respuesta en la base de la campaña.'} · Responde tú y luego pulsa Liberar.</p>
                    </div>
                  </div>
                )}
                {selected.noah_suggestion?.text && (
                  <div className="mb-2 p-3 rounded-2xl bg-brand-green/10 border border-brand-green/30" data-testid="noah-suggestion-card">
                    <div className="flex items-center gap-2 text-xs font-semibold text-brand-deep dark:text-brand-green mb-1">
                      <Sparkles className="w-3.5 h-3.5" /> Sugerencia de Noah (Copiloto)
                      <button onClick={handleDiscardSuggestion} className="ml-auto text-muted-foreground hover:text-foreground" data-testid="suggestion-discard-btn"><X className="w-3.5 h-3.5" /></button>
                    </div>
                    <p className="text-sm whitespace-pre-wrap" data-testid="noah-suggestion-text">{selected.noah_suggestion.text}</p>
                    {selected.noah_suggestion.actions?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {selected.noah_suggestion.actions.map((a, i) => (
                          <Badge key={i} variant="outline" className="text-[10px]" data-testid={`suggestion-action-${a.action}`}>{a.action}: {a.value}</Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="bg-brand-green text-white" disabled={sending} onClick={() => handleAcceptSuggestion()} data-testid="suggestion-send-btn">
                        <Send className="w-3.5 h-3.5 mr-1" /> Enviar tal cual
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setReply(selected.noah_suggestion.text)} data-testid="suggestion-edit-btn">
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Editar antes
                      </Button>
                    </div>
                  </div>
                )}
                {selected.campaign?.noah_mode === 'assist' && !selected.noah_suggestion?.text && (
                  <Button size="sm" variant="outline" className="mb-2" onClick={handleSuggest} disabled={suggesting} data-testid="suggest-reply-btn">
                    {suggesting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Pedir sugerencia a Noah
                  </Button>
                )}
                {selected.handled_by === 'noah' && !selected.ai_paused && selected.campaign?.noah_mode !== 'assist' && (
                  <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                    <Bot className="w-3.5 h-3.5 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                    Noah está atendiendo esta conversación. Tu mensaje pausará su respuesta automática.
                  </div>
                )}
                <div className="flex gap-2">
                  <StickerPicker onPick={handleSendSticker} disabled={sending} />
                  <Textarea
                    data-testid="reply-textarea"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Escribe una respuesta…"
                    rows={1}
                    className="min-h-[40px] max-h-32 bg-muted border-none resize-none"
                  />
                  <Button
                    data-testid="reply-send-btn"
                    onClick={handleSend}
                    disabled={sending || !reply.trim()}
                    className="bg-primary hover:opacity-90 self-end"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </main>
      </div>

      {/* ---------- Ticket dialog ---------- */}
      <Dialog open={ticketOpen} onOpenChange={setTicketOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Crear ticket / escalación</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Asunto</Label>
              <Input data-testid="ticket-subject" value={ticketForm.subject} onChange={(e) => setTicketForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Ej: Cliente reporta pago fallido" />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea data-testid="ticket-description" rows={3} value={ticketForm.description} onChange={(e) => setTicketForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Prioridad</Label>
                <Select value={ticketForm.priority} onValueChange={(v) => setTicketForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baja</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoría</Label>
                <Select value={ticketForm.category} onValueChange={(v) => setTicketForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">Venta</SelectItem>
                    <SelectItem value="support">Soporte</SelectItem>
                    <SelectItem value="complaint">Reclamo</SelectItem>
                    <SelectItem value="technical">Técnico</SelectItem>
                    <SelectItem value="billing">Facturación</SelectItem>
                    <SelectItem value="info">Información</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="ticket-create-btn" disabled={!ticketForm.subject.trim()} onClick={handleCreateTicket} className="bg-primary">Crear ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
